/**
 * a11y-overlay.js
 * Shared focus management for modal overlays (menu drawer, filters drawer, quick add, size guide).
 *
 * While an overlay is open:
 *  - Tab / Shift+Tab cycle inside it (focusable elements are re-read on every key press,
 *    so content that re-renders while open, e.g. variant pickers, stays inside the trap)
 *  - the rest of the page is made `inert` (unreachable by keyboard and hidden from screen readers)
 *  - Escape calls the overlay's own close routine
 * On release, only the elements this module made inert are restored, and focus can return to the opener.
 *
 * Usage:
 *   const handle = A11yOverlay.trap(containerEl, { opener, initialFocus, onEscape });
 *   handle.release({ restoreFocus: true });
 */
(function () {
  if (window.A11yOverlay) return;

  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'iframe',
    'summary',
    '[contenteditable="true"]',
    '[tabindex]:not([tabindex^="-"])',
  ].join(',');

  // Never made inert: click-to-close overlays and the cookie consent banner.
  const KEEP_ACTIVE_SELECTOR = [
    '#MenuDrawer-Overlay',
    '.sizeguide-overlay',
    '.modal-overlay',
    '[id^="cky-"]',
    '[class*="cky-"]',
    '[data-a11y-overlay-keep]',
  ].join(',');

  const stack = [];

  function isVisible(el) {
    if (!(el.offsetWidth || el.offsetHeight || el.getClientRects().length)) return false;
    return window.getComputedStyle(el).visibility !== 'hidden';
  }

  function getFocusable(container) {
    return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
      (el) => !el.closest('[inert]') && !el.closest('[data-a11y-overlay-skipped]') && isVisible(el)
    );
  }

  // Controls inside the container that must stay out of the Tab cycle while it is open
  // (e.g. the filters <summary> opener and its close span). They stay clickable; their previous
  // tabindex / aria-hidden values are restored on release.
  function skipElements(container, selector) {
    if (!selector) return [];
    return Array.from(container.querySelectorAll(selector)).map((el) => {
      const record = {
        el,
        tabindex: el.getAttribute('tabindex'),
        ariaHidden: el.getAttribute('aria-hidden'),
      };
      el.setAttribute('tabindex', '-1');
      el.setAttribute('aria-hidden', 'true');
      el.setAttribute('data-a11y-overlay-skipped', '');
      return record;
    });
  }

  function restoreSkipped(records) {
    records.forEach(({ el, tabindex, ariaHidden }) => {
      if (tabindex === null) el.removeAttribute('tabindex');
      else el.setAttribute('tabindex', tabindex);
      if (ariaHidden === null) el.removeAttribute('aria-hidden');
      else el.setAttribute('aria-hidden', ariaHidden);
      el.removeAttribute('data-a11y-overlay-skipped');
    });
  }

  function backgroundOf(container) {
    const elements = [];
    for (let node = container; node && node !== document.body; node = node.parentElement) {
      const parent = node.parentElement;
      if (!parent) break;
      Array.from(parent.children).forEach((sibling) => {
        if (sibling === node) return;
        if (['SCRIPT', 'STYLE', 'LINK', 'TEMPLATE'].includes(sibling.tagName)) return;
        if (sibling.matches(KEEP_ACTIVE_SELECTOR)) return;
        elements.push(sibling);
      });
    }
    return elements;
  }

  // Only make the container itself focusable when it has nothing focusable inside, and undo it on release.
  // (A tabindex on <details> stops Chromium from tabbing to its <summary>.)
  function focusContainer(entry) {
    if (!entry.container.hasAttribute('tabindex')) {
      entry.container.setAttribute('tabindex', '-1');
      entry.addedTabindex = true;
    }
    entry.container.focus({ preventScroll: true });
  }

  function onKeydown(event) {
    const top = stack[stack.length - 1];
    if (!top) return;

    if (event.key === 'Escape') {
      if (typeof top.onEscape === 'function') {
        event.preventDefault();
        top.onEscape(event);
      }
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = getFocusable(top.container);
    if (!focusable.length) {
      event.preventDefault();
      focusContainer(top);
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (!top.container.contains(active)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
    } else if (event.shiftKey && (active === first || active === top.container)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function onFocusIn(event) {
    const top = stack[stack.length - 1];
    if (!top || top.container.contains(event.target)) return;
    const focusable = getFocusable(top.container);
    if (focusable[0]) focusable[0].focus();
    else focusContainer(top);
  }

  function trap(container, options = {}) {
    if (!container) return { release() {} };

    // Re-trapping the same container replaces the previous entry.
    const existing = stack.find((entry) => entry.container === container);
    if (existing) existing.release({ restoreFocus: false });

    const entry = {
      container,
      opener: options.opener || (document.activeElement instanceof HTMLElement ? document.activeElement : null),
      onEscape: options.onEscape,
      inerted: [],
      skipped: [],
      released: false,
      release({ restoreFocus = true } = {}) {
        if (entry.released) return;
        entry.released = true;
        entry.inerted.forEach((el) => el.removeAttribute('inert'));
        entry.inerted = [];
        restoreSkipped(entry.skipped);
        entry.skipped = [];
        if (entry.addedTabindex) {
          entry.container.removeAttribute('tabindex');
          entry.addedTabindex = false;
        }
        const index = stack.indexOf(entry);
        if (index !== -1) stack.splice(index, 1);
        if (!stack.length) {
          document.removeEventListener('keydown', onKeydown, true);
          document.removeEventListener('focusin', onFocusIn, true);
        }
        if (restoreFocus && entry.opener && document.contains(entry.opener)) {
          entry.opener.focus({ preventScroll: true });
        }
      },
    };

    entry.skipped = skipElements(container, options.skip);
    entry.inerted = backgroundOf(container).filter((el) => !el.hasAttribute('inert'));
    entry.inerted.forEach((el) => el.setAttribute('inert', ''));

    if (!stack.length) {
      document.addEventListener('keydown', onKeydown, true);
      document.addEventListener('focusin', onFocusIn, true);
    }
    stack.push(entry);

    window.requestAnimationFrame(() => {
      if (entry.released) return;
      const focusTarget = options.initialFocus || getFocusable(container)[0];
      if (focusTarget) focusTarget.focus({ preventScroll: true });
      else focusContainer(entry);
    });

    return entry;
  }

  window.A11yOverlay = { trap, getFocusable };
})();
